/**
 * Comprehensive ISO 4217 currency registry, metadata, and intelligent
 * context-aware detection for ecommerce web pages and the manual picker.
 */

export interface CurrencyInfo {
  code: string;
  name: string;
  nameAr: string;
  symbol: string;
  abbreviations: string[];
  decimals?: number;
}

export const CURRENCIES: CurrencyInfo[] = [
  {
    code: 'SAR',
    name: 'Saudi Riyal',
    nameAr: 'ريال سعودي',
    symbol: 'ر.س',
    abbreviations: ['SAR', 'SR', 'ر.س', 'رس', 'ريال سعودي', 'ريال', '⃁', '﷼', 'Saudi Riyal'],
    decimals: 2,
  },
  {
    code: 'AED',
    name: 'UAE Dirham',
    nameAr: 'درهم إماراتي',
    symbol: 'د.إ',
    abbreviations: [
      'AED',
      'Dhs',
      'DH',
      'د.إ',
      'د.ا',
      'درهم إماراتي',
      'درهم',
      'UAE Dirham',
      'Dirham',
      '\u20C3',
    ],
    decimals: 2,
  },
  {
    code: 'EGP',
    name: 'Egyptian Pound',
    nameAr: 'جنيه مصري',
    symbol: 'ج.م',
    abbreviations: [
      'EGP',
      'LE',
      'L.E.',
      'ج.م',
      'جم',
      'جنيه مصري',
      'جنيه',
      'Egyptian Pound',
    ],
    decimals: 2,
  },
  {
    code: 'USD',
    name: 'US Dollar',
    nameAr: 'دولار أمريكي',
    symbol: '$',
    abbreviations: ['USD', '$', 'US$', 'U.S. Dollar', 'US Dollar', 'دولار أمريكي', 'دولار'],
    decimals: 2,
  },
  {
    code: 'EUR',
    name: 'Euro',
    nameAr: 'يورو',
    symbol: '€',
    abbreviations: ['EUR', '€', 'Euro', 'يورو'],
    decimals: 2,
  },
  {
    code: 'GBP',
    name: 'British Pound',
    nameAr: 'جنيه إسترليني',
    symbol: '£',
    abbreviations: ['GBP', '£', 'British Pound', 'Pound Sterling', 'جنيه إسترليني'],
    decimals: 2,
  },
  {
    code: 'KWD',
    name: 'Kuwaiti Dinar',
    nameAr: 'دينار كويتي',
    symbol: 'د.ك',
    abbreviations: ['KWD', 'KD', 'د.ك', 'دك', 'دينار كويتي', 'Kuwaiti Dinar'],
    decimals: 3,
  },
  {
    code: 'QAR',
    name: 'Qatari Riyal',
    nameAr: 'ريال قطري',
    symbol: 'ر.ق',
    abbreviations: ['QAR', 'QR', 'ر.ق', 'رق', 'ريال قطري', 'Qatari Riyal'],
    decimals: 2,
  },
  {
    code: 'BHD',
    name: 'Bahraini Dinar',
    nameAr: 'دينار بحريني',
    symbol: 'د.ب',
    abbreviations: ['BHD', 'BD', 'د.ب', 'دب', 'دينار بحريني', 'Bahraini Dinar'],
    decimals: 3,
  },
  {
    code: 'OMR',
    name: 'Omani Rial',
    nameAr: 'ريال عماني',
    symbol: 'ر.ع',
    abbreviations: ['OMR', 'RO', 'ر.ع', 'رع', 'ريال عماني', 'Omani Rial'],
    decimals: 3,
  },
  {
    code: 'JOD',
    name: 'Jordanian Dinar',
    nameAr: 'دينار أردني',
    symbol: 'د.أ',
    abbreviations: ['JOD', 'JD', 'د.أ', 'دا', 'دينار أردني', 'Jordanian Dinar'],
    decimals: 3,
  },
  {
    code: 'CAD',
    name: 'Canadian Dollar',
    nameAr: 'دولار كندي',
    symbol: 'CA$',
    abbreviations: ['CAD', 'C$', 'CA$', 'Canadian Dollar', 'دولار كندي'],
    decimals: 2,
  },
  {
    code: 'AUD',
    name: 'Australian Dollar',
    nameAr: 'دولار أسترالي',
    symbol: 'A$',
    abbreviations: ['AUD', 'A$', 'AU$', 'Australian Dollar', 'دولار أسترالي'],
    decimals: 2,
  },
  {
    code: 'JPY',
    name: 'Japanese Yen',
    nameAr: 'ين ياباني',
    symbol: '¥',
    abbreviations: ['JPY', '¥', 'Japanese Yen', 'ين ياباني'],
    decimals: 0,
  },
  {
    code: 'INR',
    name: 'Indian Rupee',
    nameAr: 'روبية هندية',
    symbol: '₹',
    abbreviations: ['INR', '₹', 'Rs', 'Indian Rupee', 'روبية هندية'],
    decimals: 2,
  },
  {
    code: 'TRY',
    name: 'Turkish Lira',
    nameAr: 'ليرة تركية',
    symbol: '₺',
    abbreviations: ['TRY', 'TL', '₺', 'Turkish Lira', 'ليرة تركية'],
    decimals: 2,
  },
  {
    code: 'CNY',
    name: 'Chinese Yuan',
    nameAr: 'يوان صيني',
    symbol: '¥',
    abbreviations: ['CNY', 'RMB', '¥', 'Chinese Yuan', 'يوان صيني'],
    decimals: 2,
  },
  {
    code: 'CHF',
    name: 'Swiss Franc',
    nameAr: 'فرنك سويسري',
    symbol: 'CHF',
    abbreviations: ['CHF', 'Swiss Franc', 'فرنك سويسري'],
    decimals: 2,
  },
  {
    code: 'BRL',
    name: 'Brazilian Real',
    nameAr: 'ريال برازيلي',
    symbol: 'R$',
    abbreviations: ['BRL', 'R$', 'Brazilian Real'],
    decimals: 2,
  },
  {
    code: 'MXN',
    name: 'Mexican Peso',
    nameAr: 'بيزو مكسيكي',
    symbol: 'Mex$',
    abbreviations: ['MXN', 'Mex$', 'Mexican Peso'],
    decimals: 2,
  },
  {
    code: 'SGD',
    name: 'Singapore Dollar',
    nameAr: 'دولار سنغافوري',
    symbol: 'S$',
    abbreviations: ['SGD', 'S$', 'Singapore Dollar'],
    decimals: 2,
  },
  {
    code: 'NZD',
    name: 'New Zealand Dollar',
    nameAr: 'دولار نيوزيلندي',
    symbol: 'NZ$',
    abbreviations: ['NZD', 'NZ$', 'New Zealand Dollar'],
    decimals: 2,
  },
  {
    code: 'HKD',
    name: 'Hong Kong Dollar',
    nameAr: 'دولار هونغ كونغ',
    symbol: 'HK$',
    abbreviations: ['HKD', 'HK$', 'Hong Kong Dollar'],
    decimals: 2,
  },
  {
    code: 'SEK',
    name: 'Swedish Krona',
    nameAr: 'كرونة سويدية',
    symbol: 'kr',
    abbreviations: ['SEK', 'kr', 'Swedish Krona'],
    decimals: 2,
  },
  {
    code: 'NOK',
    name: 'Norwegian Krone',
    nameAr: 'كرونة نرويجية',
    symbol: 'kr',
    abbreviations: ['NOK', 'kr', 'Norwegian Krone'],
    decimals: 2,
  },
  {
    code: 'DKK',
    name: 'Danish Krone',
    nameAr: 'كرونة دنماركية',
    symbol: 'kr',
    abbreviations: ['DKK', 'kr', 'Danish Krone'],
    decimals: 2,
  },
  {
    code: 'KRW',
    name: 'South Korean Won',
    nameAr: 'وون كوري جنوبي',
    symbol: '₩',
    abbreviations: ['KRW', '₩', 'Korean Won'],
    decimals: 0,
  },
  {
    code: 'ZAR',
    name: 'South African Rand',
    nameAr: 'راند جنوب أفريقي',
    symbol: 'R',
    abbreviations: ['ZAR', 'South African Rand'],
    decimals: 2,
  },
];

// Pre-compiled list of abbreviation -> CurrencyInfo sorted by length descending
interface TokenMapItem {
  token: string;
  isSymbol: boolean;
  currency: CurrencyInfo;
}

const TOKEN_MAP: TokenMapItem[] = (() => {
  const items: TokenMapItem[] = [];
  for (const c of CURRENCIES) {
    for (const abbr of c.abbreviations) {
      items.push({
        token: abbr,
        isSymbol: !/^[A-Za-z0-9]+$/.test(abbr),
        currency: c,
      });
    }
  }
  // Sort descending by token length so longer names match first (e.g. "Saudi Riyal" before "SAR")
  items.sort((a, b) => b.token.length - a.token.length);
  return items;
})();

/**
 * Return all supported currencies.
 */
export function getAllCurrencies(): CurrencyInfo[] {
  return CURRENCIES;
}

/**
 * Find currency by 3-letter ISO code.
 */
export function findCurrencyByCode(code: string): CurrencyInfo | undefined {
  const upper = code.trim().toUpperCase();
  return CURRENCIES.find((c) => c.code === upper);
}

/**
 * Escape string for RegExp pattern matching.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Detect currency from an arbitrary text snippet (e.g. "₶ 1399.99", "SAR 450", "120 ر.س", "19.99 $").
 */
export function detectCurrencyFromText(text: string): CurrencyInfo | undefined {
  if (!text || !text.trim()) return undefined;

  for (const item of TOKEN_MAP) {
    if (item.isSymbol) {
      if (text.includes(item.token)) {
        return item.currency;
      }
    } else {
      // Word boundary regex for alphanumeric tokens
      const regex = new RegExp(`(^|[^a-zA-Z0-9])${escapeRegex(item.token)}([^a-zA-Z0-9]|$)`, 'i');
      if (regex.test(text)) {
        return item.currency;
      }
    }
  }

  return undefined;
}

/**
 * Detect currency from a webpage URL (Noon, Amazon, eBay, regional TLDs, etc.).
 */
export function detectCurrencyFromUrl(urlStr: string): CurrencyInfo | undefined {
  if (!urlStr) return undefined;

  try {
    const parsed = new URL(urlStr);
    const host = parsed.hostname.toLowerCase();
    const pathname = parsed.pathname.toLowerCase();

    // 1. Noon regional stores
    if (host.includes('noon.com')) {
      if (pathname.includes('/saudi') || pathname.includes('/sa-') || pathname.startsWith('/sa/')) {
        return findCurrencyByCode('SAR');
      }
      if (pathname.includes('/uae') || pathname.includes('/ae-') || pathname.startsWith('/ae/')) {
        return findCurrencyByCode('AED');
      }
      if (pathname.includes('/egypt') || pathname.includes('/eg-') || pathname.startsWith('/eg/')) {
        return findCurrencyByCode('EGP');
      }
      return findCurrencyByCode('SAR');
    }

    // 2. Amazon regional domains
    if (host.includes('amazon.')) {
      if (host.endsWith('.sa')) return findCurrencyByCode('SAR');
      if (host.endsWith('.ae')) return findCurrencyByCode('AED');
      if (host.endsWith('.eg')) return findCurrencyByCode('EGP');
      if (host.endsWith('.co.uk')) return findCurrencyByCode('GBP');
      if (host.endsWith('.de') || host.endsWith('.fr') || host.endsWith('.it') || host.endsWith('.es')) {
        return findCurrencyByCode('EUR');
      }
      if (host.endsWith('.ca')) return findCurrencyByCode('CAD');
      if (host.endsWith('.com.au')) return findCurrencyByCode('AUD');
      if (host.endsWith('.co.jp')) return findCurrencyByCode('JPY');
      if (host.endsWith('.in')) return findCurrencyByCode('INR');
      if (host.endsWith('.com')) return findCurrencyByCode('USD');
    }

    // 3. eBay regional domains
    if (host.includes('ebay.')) {
      if (host.endsWith('.co.uk')) return findCurrencyByCode('GBP');
      if (host.endsWith('.de') || host.endsWith('.fr') || host.endsWith('.it') || host.endsWith('.es')) {
        return findCurrencyByCode('EUR');
      }
      if (host.endsWith('.ca')) return findCurrencyByCode('CAD');
      if (host.endsWith('.com.au')) return findCurrencyByCode('AUD');
    }

    // 4. Country TLD matching
    if (host.endsWith('.sa')) return findCurrencyByCode('SAR');
    if (host.endsWith('.ae')) return findCurrencyByCode('AED');
    if (host.endsWith('.eg')) return findCurrencyByCode('EGP');
    if (host.endsWith('.uk')) return findCurrencyByCode('GBP');
    if (host.endsWith('.ca')) return findCurrencyByCode('CAD');
    if (host.endsWith('.au')) return findCurrencyByCode('AUD');
    if (host.endsWith('.jp')) return findCurrencyByCode('JPY');
    if (host.endsWith('.in')) return findCurrencyByCode('INR');
    if (host.endsWith('.tr')) return findCurrencyByCode('TRY');
  } catch {
    // Ignore invalid URL
  }

  return undefined;
}

/**
 * Intelligent context-aware currency detection inspecting:
 * 1. Target element text & attributes
 * 2. Parent & price container text & child nodes (like svg aria-label, img alt)
 * 3. Page Schema JSON-LD & meta tags (og:price:currency, priceCurrency)
 * 4. Page URL
 */
export function detectCurrencyFromContext(
  element: HTMLElement | null | undefined,
  urlStr?: string
): CurrencyInfo | undefined {
  // 1. Check element's own text
  if (element) {
    const text = (element.innerText || element.textContent || '').trim();
    const fromText = detectCurrencyFromText(text);
    if (fromText) return fromText;

    // Check attributes (e.g. data-currency="SAR", aria-label="1399.99 SAR")
    const attrs = ['data-currency', 'data-price-currency', 'aria-label', 'title'];
    for (const attr of attrs) {
      const val = element.getAttribute(attr);
      if (val) {
        const fromAttr = detectCurrencyFromText(val) || findCurrencyByCode(val);
        if (fromAttr) return fromAttr;
      }
    }

    // 2. Check parent / closest price container
    const priceContainer = element.closest<HTMLElement>(
      '[class*="price"], [id*="price"], [itemprop*="price"], [data-testid*="price"]'
    );
    if (priceContainer) {
      const containerText = (priceContainer.innerText || priceContainer.textContent || '').trim();
      const fromContainer = detectCurrencyFromText(containerText);
      if (fromContainer) return fromContainer;

      // Check svg / img children within price container (often stores use svg currency icons with aria-label or alt)
      const icons = priceContainer.querySelectorAll('svg, img, i, span');
      for (const icon of Array.from(icons)) {
        const iconDesc =
          icon.getAttribute('aria-label') ||
          icon.getAttribute('title') ||
          icon.getAttribute('alt') ||
          icon.className ||
          '';
        const fromIcon = detectCurrencyFromText(iconDesc) || findCurrencyByCode(iconDesc);
        if (fromIcon) return fromIcon;
      }
    }

    // 3. Check siblings of target
    if (element.parentElement) {
      for (const sibling of Array.from(element.parentElement.children)) {
        if (sibling === element) continue;
        const sibText = (sibling.textContent || '').trim();
        const fromSib = detectCurrencyFromText(sibText);
        if (fromSib) return fromSib;
      }
    }
  }

  // 4. Check document meta tags
  if (typeof document !== 'undefined') {
    const metaCurrency =
      document.querySelector('meta[property="og:price:currency"]')?.getAttribute('content') ||
      document.querySelector('meta[itemprop="priceCurrency"]')?.getAttribute('content') ||
      document.querySelector('meta[name="twitter:data2"]')?.getAttribute('content');

    if (metaCurrency) {
      const fromMeta = findCurrencyByCode(metaCurrency) || detectCurrencyFromText(metaCurrency);
      if (fromMeta) return fromMeta;
    }

    // 5. Check JSON-LD
    try {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (const script of Array.from(scripts)) {
        const content = script.textContent;
        if (content && content.includes('priceCurrency')) {
          const match = content.match(/"priceCurrency"\s*:\s*"([A-Z]{3})"/i);
          if (match && match[1]) {
            const fromLd = findCurrencyByCode(match[1]);
            if (fromLd) return fromLd;
          }
        }
      }
    } catch {
      // Ignore JSON-LD parse errors
    }
  }

  // 6. Check Page URL
  const targetUrl = urlStr || (typeof window !== 'undefined' ? window.location?.href : '');
  if (targetUrl) {
    const fromUrl = detectCurrencyFromUrl(targetUrl);
    if (fromUrl) return fromUrl;
  }

  return undefined;
}
